import React, { useState } from "react";
import classNames from "classnames";

import Image from "components/Image";
import { Sizes } from "components/Image/Image";
import LightBox from "components/LightBox";

/**
 * Photo galleries display images in columns.
 * 3 columns on desktop, 2 columns on tablet, 1 column on mobile
 */

const PhotoGallery: React.FC<{
  className?: string;
  eagerLoadWhen?: (image: string, index: number) => boolean;
  images: { imageUrl: string; alt?: string | null }[];
  emptyStateMessage?: string;
  photoClassName?: string;
  sizes?: Sizes;
  onClick?: () => void;
  onMoveNextClick?: (index: number) => void;
  onMovePrevClick?: (index: number) => void;
}> = ({
  images,
  photoClassName,
  className,
  eagerLoadWhen = () => false,
  emptyStateMessage = "No photos available.",
  onClick = () => {},
  onMoveNextClick = () => {},
  onMovePrevClick = () => {},
  sizes = [436, 640, 355, 320, null, 640],
  ...others
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const handleLightBoxOpen = (index: number) => {
    setLightboxOpen(true);
    setPhotoIndex(index);
    onClick();
  };

  const handleLightBoxClose = () => {
    setLightboxOpen(false);
  };

  const imageCount = images.length;

  return (
    <>
      {imageCount === 0 && (
        <div className="m-section text-center">{emptyStateMessage}</div>
      )}
      <div
        className={classNames("flex flex-wrap justify-between", className)}
        {...others}
      >
        {images.map((image, index) => {
          const eagerLoad = eagerLoadWhen(image.imageUrl, index);

          return (
            <button
              className={classNames(
                "col md:col-1/2 lg:col-1/3 mb-md overflow-hidden",
                photoClassName,
              )}
              key={index}
              onClick={() => handleLightBoxOpen(index)}
              aria-label={`Photo ${index} of ${imageCount}`}
            >
              <Image
                alt={image?.alt || ""}
                disableLazyLoad={eagerLoad}
                ratio="square"
                sizes={sizes}
                src={image.imageUrl}
              />
            </button>
          );
        })}
        {lightboxOpen && (
          <LightBox
            data-testid="photo-gallery-lightbox" /* see https://kentcdodds.com/blog/making-your-ui-tests-resilient-to-change */
            images={images}
            initialPhotoIndex={photoIndex}
            onToggleLightboxClick={handleLightBoxClose}
            onMoveNextClick={onMoveNextClick}
            onMovePrevClick={onMovePrevClick}
          />
        )}
      </div>
    </>
  );
};

export default PhotoGallery;
